import { provideHttpClient } from '@angular/common/http';
import { ApplicationConfig, importProvidersFrom, inject, isDevMode, provideAppInitializer } from '@angular/core';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { provideFuse } from '@fuse';
import { TranslocoService, provideTransloco } from '@jsverse/transloco';
import { appRoutes } from 'app/app.routes';
import { provideAuth } from 'app/core/auth/auth.provider';
import { ImpersonationService } from 'app/core/auth/impersonation.service';
import { PermissionService } from 'app/core/auth/permission.service';
import { provideIcons } from 'app/core/icons/icons.provider';
import { MockApiService } from 'app/mock-api';
import { LookupService } from 'app/services/lookup.service';
import { KeycloakAngularModule, KeycloakService } from 'keycloak-angular';
import { MarkdownModule } from 'ngx-markdown';
import { firstValueFrom } from 'rxjs';
import { TranslocoHttpLoader } from './core/transloco/transloco.http-loader';
import keycloakOptions from './keycloack.config';

export const appConfig: ApplicationConfig = {
    providers: [
        provideAnimations(),
        provideHttpClient(),
        provideRouter(appRoutes, withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })),

        // Material Date Adapter
        {
            provide: DateAdapter,
            useClass: LuxonDateAdapter,
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: {
                    dateInput: 'D',
                },
                display: {
                    dateInput: 'DDD',
                    monthYearLabel: 'LLL yyyy',
                    dateA11yLabel: 'DD',
                    monthYearA11yLabel: 'LLLL yyyy',
                },
            },
        },

        // KeyCloack
        importProvidersFrom(KeycloakAngularModule, MarkdownModule.forRoot()),
        provideAppInitializer(async () => {
            const keycloak = inject(KeycloakService);
            const permissionService = inject(PermissionService);
            const lookupService = inject(LookupService);
            // Instantiating the service is what restores an active impersonation from sessionStorage.
            // Elsewhere that happens through AuthGuard or the classic layout, neither of which runs on
            // the public scan routes: without this the scanner would keep the real user's roles and token.
            const impersonationService = inject(ImpersonationService);
            try {
                await keycloak.init({
                    config: keycloakOptions.config,
                    initOptions: keycloakOptions.initOptions,
                });
                const refreshToken = keycloak.getKeycloakInstance().refreshToken;
                if (refreshToken) localStorage.setItem('refreshToken', refreshToken);

                const token_1 = keycloak.getKeycloakInstance().token;
                if (token_1) localStorage.setItem('token', token_1);

                // Load permissions from token so they're available on all routes (including error pages).
                // While impersonating they already come from the impersonated token: reloading the real
                // one here would silently restore the original user's roles.
                if (!impersonationService.isImpersonating) {
                    permissionService.loadFromToken();
                }

                // Load lookup data after auth is complete
                await lookupService.initialize();
            } catch (err) {
                console.error('Keycloak init failed', err);
            }
        }),
        // Transloco Config
        provideTransloco({
            config: {
                availableLangs: [
                    {
                        id: 'en',
                        label: 'English',
                    },
                    {
                        id: 'it',
                        label: 'Italiano',
                    },
                ],
                defaultLang: 'en',
                fallbackLang: 'en',
                reRenderOnLangChange: true,
                prodMode: !isDevMode(),
            },
            loader: TranslocoHttpLoader,
        }),
        provideAppInitializer(() => {
            const translocoService = inject(TranslocoService);
            const defaultLang = translocoService.getDefaultLang();
            translocoService.setActiveLang(defaultLang);

            return firstValueFrom(translocoService.load(defaultLang));
        }),
        // Fuse
        provideAuth(),
        provideIcons(),
        provideFuse({
            mockApi: {
                delay: 0,
                service: MockApiService,
            },
            fuse: {
                layout: 'classic',
                scheme: 'dark',
                screens: {
                    sm: '600px',
                    md: '960px',
                    lg: '1280px',
                    xl: '1440px',
                },
                theme: 'theme-brand',
                themes: [
                    {
                        id: 'theme-default',
                        name: 'Default',
                    },
                    {
                        id: 'theme-brand',
                        name: 'Brand',
                    },
                    {
                        id: 'theme-teal',
                        name: 'Teal',
                    },
                    {
                        id: 'theme-rose',
                        name: 'Rose',
                    },
                    {
                        id: 'theme-purple',
                        name: 'Purple',
                    },
                    {
                        id: 'theme-amber',
                        name: 'Amber',
                    },
                ],
            },
        }),
    ],
};
